<template>
  <div>
    <LoadingSpinner v-if="loading" :full="true" />
    <div v-else class="dashboard">
      <div class="card dashboard-summary">
        <div class="dashboard-summary__info">
          <span class="material-icons">calendar_today</span>
          {{ dashboard.quick_overview_statistics.summary_date }}
        </div>
        <div class="dashboard-summary__info">
          <span class="material-icons">date_range</span>
          {{ dashboard.quick_overview_statistics.quarter }}
        </div>
        <div class="dashboard-summary__info dashboard-summary__info">
          <span class="material-icons">scale</span>
          {{ this.decimal(dashboard.quick_overview_statistics.total_material_weight_t) }}
          t
        </div>
        <div class="dashboard-summary__info">
          <span class="material-icons">format_list_bulleted</span>
          {{
            $t('reporting.industry.doneOrders', {
              count: dashboard.quick_overview_statistics.done_order_count,
            })
          }}
        </div>
      </div>
      <div class="dashboard__inner">
        <div class="dashboard__left">
          <OrderCosts
            :order-costs-data="dashboard.order_costs_data"
            :date="dashboard.quick_overview_statistics.month_and_year"
          />

          <Materials
            :areas-with-fractions="dashboard.material_weights_data.fraction_data_by_area"
            :date="dashboard.quick_overview_statistics.month_and_year"
          />

          <!--
          The FillRates component uses a binity-api endpoint that loads too much information. The request (for Tesla)
          takes over a minute to load and is not easily fixable. So we remove the component for now.
          -->
          <!-- <FillRates :companyUuid="companyUser.company_uuid" /> -->
          <Inventory :companyUuid="companyUser.company_uuid" />
        </div>

        <div class="dashboard__right">
          <div class="card">
            <div class="card__header">
              <span class="card__title">
                {{ $t('reporting.industry.material') }}
              </span>
            </div>
            <div class="card__subtitle">
              <span>{{ dashboard.quick_overview_statistics.month_and_year }}</span>
            </div>
            <MaterialWeightsGraph :material-weights-data="dashboard.material_weights_data.weight_data_by_fraction" />
          </div>
          <div class="card">
            <div class="card__header">
              <span class="card__title">
                {{ $t('reporting.industry.orderCosts') }}
              </span>
            </div>
            <div class="card__subtitle">
              <span>{{ dashboard.quick_overview_statistics.month_and_year }}</span>
            </div>
            <OrderCostsGraph :order-costs-data="dashboard.order_costs_data" />
          </div>

          <div class="card">
            <div class="card__header">
              <span class="card__title">
                {{ $t('meta.containerLocations') }}
              </span>
            </div>
            <div class="card__subtitle">
              <span>{{
                $t('reporting.industry.lastDate', {
                  date: dashboard.quick_overview_statistics.full_date,
                })
              }}</span>
            </div>
            <div class="counter-row small">
              <span class="material-icons">delete</span>
              <Counter :number="dashboard.container_related_data.containers_count" :title="$t('meta.containers')" />
            </div>
            <div class="counter-row">
              <Counter :number="dashboard.container_related_data.areas_count" :title="$t('meta.areas')" />
              <span class="material-icons">pin_drop</span>
              <Counter :number="dashboard.container_related_data.zones_count" :title="$t('meta.zones')" />
            </div>
          </div>
          <div class="card">
            <div class="card__header">
              <span class="card__title">
                {{ $t('reporting.industry.containerMovement') }}
              </span>
            </div>
            <div class="card__subtitle">
              <span>{{ dashboard.quick_overview_statistics.month_and_year }}</span>
            </div>
            <div class="container-movements">
              <Counter
                :number="dashboard.container_related_data.positioned_containers_count"
                :title="$t('reporting.industry.positionedContainers')"
              />
              <Counter
                :number="dashboard.container_related_data.emptied_containers_count"
                :title="$t('reporting.industry.emptiedContainers')"
              />
              <span class="material-icons container-movements__icon">exit_to_app</span>
              <span class="material-icons container-movements__icon">logout</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from '@/components/LoadingSpinner/index.vue'
import Counter from '@/components/Reporting/Counter'
// import FillRates from '@/components/Reporting/Industry/FillRates'
import Inventory from '@/components/Reporting/Industry/Inventory'
import Materials from '@/components/Reporting/Industry/Materials'
import OrderCosts from '@/components/Reporting/Industry/OrderCosts'
import MaterialWeightsGraph from '@/components/Reporting/MaterialWeightsGraph'
import OrderCostsGraph from '@/components/Reporting/OrderCostsGraph'
import stringUtils from '@/components/mixins/stringUtils'

import reportingApi from '@/api/reporting'

export default {
  name: 'ReportingRUMS',
  inject: ['companyUser'],
  components: {
    Counter,
    // FillRates,
    Inventory,
    LoadingSpinner,
    Materials,
    MaterialWeightsGraph,
    OrderCosts,
    OrderCostsGraph,
  },
  mixins: [stringUtils],
  data() {
    return {
      dashboard: {
        container_related_data: {
          containers_count: 0,
          areas_count: 0,
          zones_count: 0,
          positioned_containers_count: 0,
          emptied_containers_count: 0,
        },
        order_costs_data: {},
        material_weights_data: {
          fraction_data_by_area: [],
          weight_data_by_fraction: [],
        },
        quick_overview_statistics: {},
      },
      loading: true,
    }
  },
  methods: {
    async loadData() {
      this.loading = true
      this.dashboard = await reportingApi.getForRUMS(this.companyUser.company_uuid)
      this.loading = false
    },
  },
  async mounted() {
    await this.loadData()
  },
}
</script>

<style lang="scss" scoped>
@import 'reporting-industry.scss';

.dashboard {
  &__inner {
    display: flex;
  }

  &__left,
  &__right {
    display: flex;
    flex-direction: column;
    row-gap: 1.5rem;
  }

  &__left {
    flex-basis: 70%;
  }

  &__right {
    flex-basis: 30%;
    margin-left: 1.5rem;
  }
}

.card {
  &__subtitle {
    font-size: 12px;
    margin-bottom: 0.75rem;
  }
}

.container-locations {
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: 10px;
}

.container-movements {
  display: grid;
  grid-template-columns: 1fr 1fr;
  row-gap: 16px;

  &__icon {
    justify-self: center;
    font-size: 36px;
  }
}

.dashboard {
  &-summary {
    margin-bottom: 1.5rem;
  }
}

.counter-row {
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: 8px;

  & + .counter-row {
    margin-top: 26px;
  }

  &.small {
    column-gap: 0;
  }

  .material-icons {
    font-size: 44px;
  }
}

.empty {
  flex: 1;
  font-size: 1.125rem;
  color: #a9a9a9;
  letter-spacing: 1px;
  text-align: center;
}
</style>
